<template>
  <div class="card-user">
    <div class="card-container">
      <!-- プロフィール画像 -->
      <b-card-img
        v-bind:src="profileImageUrl"
        class="profile-icon"
        crossorigin="use-credentials"
      />
      <!-- 情報表示 -->
      <div class="content-info">
        <!-- 基本情報 -->
        <div class="content-basic">
          <!-- ユーザー名 -->
          <p
            class="user-name"
            v-bind:class="{ clickable: canUserNameClick }"
            v-on:click.stop="$_onClickUserName"
          >
            {{ userName }}
          </p>
          <!-- 所属グループ -->
          <div v-if="groups.length" class="group">
            <span v-if="isTagUser" class="text">-</span>
            <template v-else>
              <span
                ref="truncTarget"
                v-ifield-b-tooltip="tooltipOptionDefault"
                class="text ellipsis"
                v-bind:class="{ trailing: otherGroupNames.length }"
              >
                {{ defaultGroupName }}
              </span>
              <template v-if="otherGroupNames.length">
                <span class="text">,</span>
                <span class="text others" v-b-tooltip="tooltipOptionOthers">
                  {{ $tc('appCardUser.others', otherGroupNames.length) }}
                </span>
              </template>
            </template>
          </div>
        </div>
        <!--
          @slot 追加表示コンテンツ
            @binding {Object} data props をラップした Object
        -->
        <slot name="content-append" v-bind:data="$props"></slot>
      </div>
      <!-- アクション -->
      <div class="content-action">
        <b-button
          v-if="isMessageAvailable"
          class="action-btn message-btn"
          size="sm"
          variant="light"
          v-ifield-b-tooltip.hover
          v-bind:title="$t('appCardUser.action.createMessage')"
          v-on:click.stop="$_showMessagePost"
        >
          <message-icon class="icon" />
        </b-button>
        <b-button
          v-if="history"
          class="action-btn history-btn"
          size="sm"
          variant="light"
          v-ifield-b-tooltip.hover
          v-bind:title="$t('appCardUser.action.showTrajectory')"
          v-on:click.stop="$_onClickHistory"
        >
          <history-icon class="icon" />
        </b-button>
      </div>
    </div>
    <!-- 新規メッセージ送信ダイアログ -->
    <message-post-dialog
      ref="message-post-dialog"
      v-bind:default-receive-users="[{ id: userId, role_id: roleId }]"
    />
  </div>
</template>

<script>
import MessagePostDialog from '@/components/message/AppMessagePostDialog.vue'
import { FUNCTION_TYPE } from '@/constants.js'
import { RestApiClient, TAG_USER_ROLE_ID } from 'ifield-core-components'
import { useIsTruncated } from '@/composables/UseIsTruncated'
import MessageIcon from '@/assets/ifield/ic_message.svg?component'
import HistoryIcon from '@/assets/ifield/ic_history.svg?component'

export default {
  name: 'AppCardUser',
  components: {
    'message-post-dialog': MessagePostDialog,
    'message-icon': MessageIcon,
    'history-icon': HistoryIcon,
  },
  inheritAttrs: false,
  props: {
    /**
     * ユーザーID
     */
    userId: {
      type: Number,
      default: null,
      required: true,
    },
    /**
     * ユーザー名
     */
    userName: {
      type: String,
      default: null,
    },
    /**
     * ユーザープロフィール画像の添付ファイルタイプ
     */
    profileImageAttachFileType: {
      type: String,
      default: '9',
    },
    /**
     * 所属グループ
     */
    groups: {
      type: Array,
      default: () => [],
    },
    /**
     * 履歴ボタンが使用可能な場合はtrue
     */
    history: {
      type: Boolean,
      default: false,
    },
    /**
     * ユーザー名をクリック可能な場合はtrue
     */
    canUserNameClick: {
      type: Boolean,
      default: false,
    },
    /**
     * ユーザーのロールID
     */
    roleId: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const { truncTarget, isTruncated } = useIsTruncated()
    return { truncTarget, isTruncated }
  },
  computed: {
    /**
     * プロフィール画像のURL
     * @type {string}
     */
    profileImageUrl: function () {
      const url = RestApiClient.createApiUrl('download_file')
      const params = url.searchParams
      params.append('id', this.userId)
      params.append('thumbnail', 1)
      if (this.profileImageAttachFileType) {
        params.append('attach_file_type', this.profileImageAttachFileType)
      }
      return url.toString()
    },
    /**
     * 対象ユーザーがタグユーザーか判定
     * @type {Boolean}
     */
    isTagUser: function () {
      return this.roleId === TAG_USER_ROLE_ID
    },
    /**
     * デフォルト扱いするグループのデータ
     * @type {Object}
     */
    defaultGroup: function () {
      if (!this.groups?.length) return null
      return this.groups.find((g) => g.default_organization) || this.groups[0]
    },
    /**
     * デフォルト表示するグループの名称
     * @type {string}
     */
    defaultGroupName: function () {
      return this.defaultGroup?.organization_name || ''
    },
    /**
     * デフォルト表示しないグループの名称
     * @type {string[]}
     */
    otherGroupNames: function () {
      if (!this.defaultGroup) return []
      const defaultGroupId =
        this.defaultGroup.organization_id || this.defaultGroup.id
      return this.groups
        .filter((g) => (g.organization_id || g.id) !== defaultGroupId)
        .map((g) => g.organization_name)
    },
    /**
     * デフォルトグループ側のツールチップオプション
     * @type {Object}
     */
    tooltipOptionDefault: function () {
      return {
        title: this.defaultGroupName,
        disabled: !this.isTruncated,
      }
    },
    /**
     * その他グループ側のツールチップオプション
     * @type {Object}
     */
    tooltipOptionOthers: function () {
      return {
        title: this.otherGroupNames.join(', '),
        placement: 'right',
        boundary: 'window',
        container: 'body',
        customClass: 'ifield-tooltip-light',
      }
    },
    /**
     * メッセージが利用可能か？
     * タグユーザーはメッセージを送信できない
     * @type {Boolean}
     */
    isMessageAvailable: function () {
      return (
        !this.isTagUser &&
        !!this.$store.getters.functionType(FUNCTION_TYPE.SEND_MESSAGE)
      )
    },
  },
  methods: {
    /**
     * 履歴ボタンがクリックされた時の処理
     */
    $_onClickHistory: function (event) {
      this.$emit('usercard-history-click', this.userId, event)
    },

    /**
     * ユーザー名がクリックされた時の処理
     */
    $_onClickUserName: function () {
      if (this.canUserNameClick) {
        this.$emit('usercard-username-click', this.userId)
      }
    },

    /**
     * メッセージ新規送信ダイアログを表示する
     */
    $_showMessagePost: function () {
      this.$refs['message-post-dialog'].showModal()
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../assets/scss/mixin' as mixin;

.card-user {
  width: 100%;
}

.card-container {
  width: 100%;
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) max-content;
  column-gap: 8px;
}

.profile-icon {
  display: block;
  border: 1px solid #eeeeee;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  align-self: flex-start;
}

.content-info {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 12px;
}

.content-basic {
  min-width: 100px;
  max-width: 100%;
  height: 100%;
  text-align: left;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: flex-start;
  row-gap: 4px;
}

.user-name {
  max-width: 100%;
  color: $text-color;
  font-size: $font-size-middle;
  font-weight: $font-weight-bold;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;

  &.clickable {
    color: $text-color-link;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
}

.group {
  max-width: 100%;
  line-height: 1;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;

  .text {
    color: #959595;
    font-size: $font-size-small;

    &.ellipsis {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &.others {
      flex-shrink: 0;
      margin-left: 4px;
      text-decoration: underline;
      cursor: pointer;
    }
  }
}

.other-tooltip {
  &.tooltip {
    opacity: 1;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.24));
  }

  &.bs-tooltip-bottom {
    ::v-deep .arrow {
      &::before {
        border-bottom-color: #ffffff;
      }
    }
  }
  &.bs-tooltip-top {
    ::v-deep .arrow {
      &::before {
        border-top-color: #ffffff;
      }
    }
  }
  &.bs-tooltip-left {
    ::v-deep .arrow {
      &::before {
        border-left-color: #ffffff;
      }
    }
  }
  &.bs-tooltip-right {
    ::v-deep .arrow {
      &::before {
        border-right-color: #ffffff;
      }
    }
  }

  ::v-deep .tooltip-inner {
    // 16.5文字 + 両側 padding 値
    max-width: calc(#{$font-size-middle} * 16.5 + 32px);
    // 11.5文字 + 上側 padding 値
    max-height: calc(#{$font-size-middle} * 1.5 * 11.5 + 8px);
    padding: 8px;
    text-align: left;
    font-size: $font-size-middle;
    color: $text-color;
    background-color: #ffffff;
    overflow-y: auto;
    @include mixin.scrollbar;
    &::-webkit-scrollbar-track {
      border-top-right-radius: $border-radius-small;
      border-bottom-right-radius: $border-radius-small;
    }
    & {
      scrollbar-gutter: stable both-edges;
    }
  }
}

.content-action {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: nowrap;
  column-gap: 8px;
}

.action-btn {
  @include mixin-button(true);
}
</style>
